import { useEffect } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useUserBalances } from '@givebacks/api/ledger';
import { centsToDollar } from 'core/Utils';
import { useLocation } from 'react-router-dom';
import { Button, Divider, Drawer, DrawerProps, FocusTrap, Group, Stack, Text } from 'ui';
import { LogoGivebacksOrange } from 'ui/Icons';

import { useNavigateToAuth } from '../../../../../hooks/useNavigateToAuth/useNavigateToAuth';
import { AuthenticatedMenuLinks, MobileMenuLinks } from './MobileMenuLinks';

const OnboardingMenuActions = () => {
  const { handleNavigateToAuth } = useNavigateToAuth();

  return (
    <Stack>
      <Button onClick={() => handleNavigateToAuth({ page: 'sign-up' })}>Get Started</Button>
      <Group gap={0} justify="center">
        <Text type="sub" c="gray">
          Already have an account?
        </Text>
        <Button
          size="sm"
          variant="subtle"
          px={'xs'}
          onClick={() => {
            handleNavigateToAuth({ page: 'sign-in' });
          }}
        >
          Sign In
        </Button>
      </Group>
    </Stack>
  );
};

const UserBalance = () => {
  const { data: balance } = useUserBalances();

  return (
    <Group gap="sm" wrap="nowrap">
      <Group gap={4}>
        <Text type="sub" c="cobalt.1">
          Available
        </Text>
        <Text type="sub" fw={600} c="white">
          {centsToDollar(balance?.available)}
        </Text>
      </Group>
      <Divider color="cobalt.1" orientation="vertical" opacity={0.5} />
      <Group gap={4}>
        <Text type="sub" c="cobalt.1">
          Pending
        </Text>
        <Text type="sub" fw={600} c="white">
          {balance?.pending?.amount ? centsToDollar(balance.pending.amount) : '$0.00'}
        </Text>
      </Group>
    </Group>
  );
};

const MobileMenuDrawerBottomComponent = () => {
  const { isAuthenticated } = useAuthentication();

  return (
    <Stack px="sm" justify="flex-end">
      {isAuthenticated ? <AuthenticatedMenuLinks /> : <OnboardingMenuActions />}
    </Stack>
  );
};

const MobileMenuDrawerHeader = () => {
  const { isAuthenticated } = useAuthentication();

  return (
    <Drawer.Header p="lg">
      <Drawer.Title>
        <Stack gap="lg" mt="36px">
          <Stack gap={'sm'}>
            <LogoGivebacksOrange fit="contain" h={24} w="fit-content" />
            {isAuthenticated && <UserBalance />}
          </Stack>
        </Stack>
      </Drawer.Title>
    </Drawer.Header>
  );
};

const MobileMenuDrawer = ({ opened, onClose }: DrawerProps) => {
  const location = useLocation();

  // NOTE: We want to close the drawer onClick on any NavLinks with a location change
  // we look for location.key change to ensure that the drawer will close onClick of both PROFILE and SETTINGS as they have the same pathname
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't want to call onClose when it changes references
  }, [location.key]);

  return (
    <Drawer.Root
      opened={opened}
      onClose={onClose}
      styles={{
        inner: { width: 305, maxHeight: '100%' },
        header: {
          background:
            'linear-gradient(246deg, #0D32AA -0.01%, #0D3DAA 20.56%, #0D53AA 68.78%, #0D5BAA 100.01%)',
          borderBottomRightRadius: '--var(mantine-radius-lg)',
        },
      }}
    >
      <Drawer.Overlay />
      <Drawer.Content
        style={{
          borderBottomRightRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
        }}
      >
        <MobileMenuDrawerHeader />
        <Drawer.Body>
          <FocusTrap.InitialFocus />
          <Stack gap={'xs'} justify="space-between" pt="lg" h="100%" mih="calc(100dvh - 156px)">
            <MobileMenuLinks />
            <MobileMenuDrawerBottomComponent />
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

export { MobileMenuDrawer };
